import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Package, CalendarCheck, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [reservations, setReservations] = useState([]);

  useEffect(() => {
    if (!loading && !user) navigate("/connexion");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get("/orders/mine").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/reservations/mine").then((r) => setReservations(r.data)).catch(() => {});
  }, [user]);

  if (!user) return null;

  const fmt = (d) => (d ? new Date(d).toLocaleDateString("fr-BE", { day: "numeric", month: "short", year: "numeric" }) : "-");

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-4 mb-10">
        <span className="grid place-items-center w-16 h-16 rounded-3xl bg-sky-500 text-white">
          <User className="w-8 h-8" />
        </span>
        <div>
          <h1 className="font-heading font-black text-3xl text-slate-900">Bonjour, {user.name}</h1>
          <p className="text-slate-500">{user.email} · <span className="capitalize">{user.role}</span></p>
        </div>
      </motion.div>

      <Tabs defaultValue="orders">
        <TabsList className="rounded-full bg-slate-100 p-1">
          <TabsTrigger value="orders" className="rounded-full data-[state=active]:bg-white" data-testid="tab-orders">
            Mes commandes
          </TabsTrigger>
          <TabsTrigger value="reservations" className="rounded-full data-[state=active]:bg-white" data-testid="tab-reservations">
            Mes réservations
          </TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="mt-6">
          {orders.length === 0 ? (
            <Empty icon={Package} text="Aucune commande pour le moment." link="/nos-activites" cta="Voir nos activités" />
          ) : (
            <div className="space-y-4">
              {orders.map((o) => (
                <div key={o.session_id} className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6" data-testid="order-row">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-heading font-extrabold text-slate-900">Commande du {fmt(o.created_at)}</p>
                      <p className="text-sm text-slate-500">{o.items?.length} article(s)</p>
                    </div>
                    <div className="text-right">
                      <p className="font-heading font-black text-xl text-slate-900">{(o.amount || 0).toFixed(2)} €</p>
                      <span className={`text-xs font-bold px-3 py-1 rounded-full ${o.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                        {o.payment_status === "paid" ? "Payé" : "En attente"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="reservations" className="mt-6">
          {reservations.length === 0 ? (
            <Empty icon={CalendarCheck} text="Aucune réservation pour le moment." link="/nos-activites" cta="Voir les ateliers" />
          ) : (
            <div className="space-y-4">
              {reservations.map((r) => (
                <div key={r.id} className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 flex items-center justify-between" data-testid="reservation-row">
                  <div>
                    <p className="font-heading font-extrabold text-slate-900">{r.item_title}</p>
                    <p className="text-sm text-slate-500 capitalize">{r.kind} · Demande du {fmt(r.created_at)}</p>
                  </div>
                  <span className="text-xs font-bold px-3 py-1 rounded-full bg-amber-100 text-amber-700">En attente</span>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Empty({ icon: Icon, text, link, cta }) {
  return (
    <div className="text-center py-16 bg-white rounded-3xl border border-slate-100">
      <span className="inline-grid place-items-center w-16 h-16 rounded-2xl bg-slate-100 text-slate-400 mb-4">
        <Icon className="w-8 h-8" />
      </span>
      <p className="text-slate-500 mb-6">{text}</p>
      <Link to={link} className="inline-block px-6 py-3 rounded-full bg-sky-500 text-white font-bold hover:bg-sky-600 transition-colors">
        {cta}
      </Link>
    </div>
  );
}
